import type AccountFacilityView from '@UI/Store/AccountFacilities/Views/AccountFacilityView';
import type OrganizationView from '@UI/Store/Organizations/Views/OrganizationView';

export type ComposedOrganization = {
	readonly id: number;
	readonly name: string;
	readonly facilities: readonly AccountFacilityView[];
};

const byName = <Item extends { readonly name: string }>(left: Item, right: Item): number =>
	left.name.localeCompare(right.name);

/**
 * Rebuilds, client-side, the grouping the API used to return from `.../assignments`: one entry per
 * organization (even with no facility — that is trimmed by the facility selector, not here), each
 * carrying its facilities sorted by name, the organizations themselves sorted by name. A facility whose
 * `organizationId` matches no organization in the list is dropped: it would otherwise produce an empty
 * group header in the selector.
 */
const composeAssignedOrganizations = (
	organizations: readonly OrganizationView[],
	facilities: readonly AccountFacilityView[],
): readonly ComposedOrganization[] => {
	const facilitiesByOrganizationId = new Map<number, AccountFacilityView[]>();

	for (const facility of facilities) {
		const group = facilitiesByOrganizationId.get(facility.organizationId);

		if (group) {
			group.push(facility);
		} else {
			facilitiesByOrganizationId.set(facility.organizationId, [facility]);
		}
	}

	return organizations
		.map((organization) => ({
			id: organization.id,
			name: organization.name,
			facilities: [...(facilitiesByOrganizationId.get(organization.id) ?? [])].sort(byName),
		}))
		.sort(byName);
};

export default composeAssignedOrganizations;
